import { inject } from '@angular/core';
import { Router, UrlTree, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { CanActivateFn } from '@angular/router';
import { FeatureCapabilitiesService } from '../services/feature-capabilities.service';
import { map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

/**
 * Feature guard that protects routes based on subscription plan features.
 *
 * Usage in routes:
 * {
 *   path: 'funnels',
 *   component: FunnelAnalysisComponent,
 *   canActivate: [featureGuard],
 *   data: { requiredFeature: 'funnel_analysis' }
 * }
 *
 * Optional upgrade redirect:
 * data: { requiredFeature: 'funnel_analysis', upgradeUrl: '/upgrade' }
 */
export const featureGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean | UrlTree> | boolean | UrlTree => {
  const featureCapabilitiesService = inject(FeatureCapabilitiesService);
  const router = inject(Router);

  const requiredFeature = route.data['requiredFeature'] as string;

  if (!requiredFeature) {
    console.warn('featureGuard: No requiredFeature specified in route data');
    return true; // Allow access if no feature is specified
  }

  // Check if capabilities are already loaded
  const currentCapabilities = featureCapabilitiesService.getCurrentCapabilities();

  if (currentCapabilities) {
    // Capabilities already loaded, check synchronously
    return checkFeatureAccess(
      featureCapabilitiesService,
      router,
      requiredFeature,
      route.data['upgradeUrl'] as string | undefined
    );
  }

  // Capabilities not loaded yet, fetch them first
  return featureCapabilitiesService.getCapabilities().pipe(
    map(() => checkFeatureAccess(
      featureCapabilitiesService,
      router,
      requiredFeature,
      route.data['upgradeUrl'] as string | undefined
    ))
  );
};

/**
 * Helper function to check feature access and return routing decision
 */
function checkFeatureAccess(
  featureCapabilitiesService: FeatureCapabilitiesService,
  router: Router,
  requiredFeature: string,
  upgradeUrl?: string
): boolean | UrlTree {
  const hasAccess = featureCapabilitiesService.hasFeature(requiredFeature);

  if (hasAccess) {
    return true;
  }

  // Feature not available, redirect to upgrade page or show error
  const redirectUrl = upgradeUrl || '/upgrade';

  console.warn(
    `Feature "${requiredFeature}" not available on current plan (${featureCapabilitiesService.getPlanName()}). ` +
    `Redirecting to ${redirectUrl}`
  );

  return router.createUrlTree([redirectUrl], {
    queryParams: {
      feature: requiredFeature,
      plan: featureCapabilitiesService.getPlanCode()
    }
  });
}

/**
 * Alternative guard that only checks feature availability without redirecting.
 * Can be used in combination with other guards.
 *
 * Usage:
 * {
 *   path: 'advanced',
 *   component: AdvancedComponent,
 *   canActivate: [authGuard, hasFeatureGuard],
 *   data: { requiredFeature: 'advanced_analytics' }
 * }
 */
export const hasFeatureGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> | boolean => {
  const featureCapabilitiesService = inject(FeatureCapabilitiesService);

  const requiredFeature = route.data['requiredFeature'] as string;

  if (!requiredFeature) {
    return true;
  }

  const currentCapabilities = featureCapabilitiesService.getCurrentCapabilities();

  if (currentCapabilities) {
    return featureCapabilitiesService.hasFeature(requiredFeature);
  }

  return featureCapabilitiesService.getCapabilities().pipe(
    map(() => featureCapabilitiesService.hasFeature(requiredFeature))
  );
};
